import { supabase } from '@/composables/auth.composable'
import { currentTenantId } from '@/composables/session'
import { langueCourante } from '@/i18n'
import { from, switchMap, map, catchError, throwError, Observable } from 'rxjs'

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public payload?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:80'

// ── Plus de tiret cadratin à l'écran, jamais ─────────────────────────────────
//
// Règle du CEO, redemandée le 24 septembre 2026. Les modèles sont nettoyés à
// la source (`sansTiretsCadratins` des fournisseurs) et le code est contrôlé au
// déploiement ; reste ce qui est DÉJÀ en base, écrit avant. Toute réponse de
// l'API passe par ici : on remplace dans le texte brut, avant de le lire, ce qui
// ne touche que des valeurs (une clé JSON n'en contient jamais). Écrit en
// échappements pour que le contrôle ne se signale pas lui-même.
const TIRETS = /[\u2013\u2014]/g
const sansTirets = (texte: string): string => texte.replace(TIRETS, '-')

// util: parse safe
function safeJsonParse(text: string) {
  const propre = sansTirets(text)
  try {
    return JSON.parse(propre)
  } catch {
    return propre
  }
}

/**
 * Construit les headers en appelant getSession() à chaque souscription.
 * getSession() retourne le token en cache si valide, ou le rafraîchit
 * automatiquement s'il est expiré — ce qui évite les 401 quand on revient
 * sur un onglet après une longue période d'inactivité.
 *
 * ── X-Orion-Tenant est OBLIGATOIRE sur /v1 ──────────────────────────────────
 *
 * Le front porte deux clients d'API : `@orion/sdk` (via `composables/api.ts`)
 * et celui-ci, repris VERBATIM de l'ancien front. Le premier envoyait déjà le
 * tenant ; celui-ci ne l'a jamais fait, parce que l'ancien backend le déduisait
 * de l'utilisateur côté serveur.
 *
 * OrionCore ne le déduit pas — délibérément : `requireTenantAccess` lit
 * l'en-tête et RIEN d'autre, parce qu'un utilisateur peut appartenir à
 * plusieurs tenants et que deviner lequel côté serveur, c'est choisir à sa
 * place. Sans l'en-tête, chaque route répond 400 `tenant_required`.
 *
 * Ce sont les 23 pages V4 qui utilisent CE client. Sans cette ligne, elles
 * échouent toutes de la même façon, sur tous les écrans — et le déploiement
 * n'aurait rien appris d'autre que ça.
 *
 * Le tenant est lu à CHAQUE souscription, comme le jeton : le figer casserait
 * le changement de tenant sans rechargement de page.
 */
export function authHeaders$(extraHeaders?: Record<string, string>): Observable<Record<string, string>> {
  return from(supabase.auth.getSession()).pipe(
    map(({ data }) => {
      const token = data.session?.access_token
      const tenantId = currentTenantId()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(extraHeaders ?? {}),
      }
      if (token) headers.Authorization = `Bearer ${token}`
      if (tenantId) headers['X-Orion-Tenant'] = tenantId
      // La langue de la personne (8 octobre 2026) : ce que l'API fait générer
      // à sa demande s'écrit dans sa langue. En-tête standard, sans préflight.
      if (!headers['Accept-Language']) headers['Accept-Language'] = langueCourante()
      return headers
    }),
  )
}

/** Streams a POST SSE endpoint, yielding each parsed event. */
export async function* streamSSE(url: string, body: unknown, token: string): AsyncGenerator<any> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try { yield JSON.parse(sansTirets(line.slice(6))) } catch { /* skip malformed */ }
      }
    }
  }
}

export function fetchJson$<T>(url: string, init?: RequestInit): Observable<T> {
  return from(fetch(url, init)).pipe(
    switchMap(async (res) => {
      const text = await res.text()
      const payload = text ? safeJsonParse(text) : null

      if (!res.ok) {
        // Le backend renvoie des messages actionnables - ils doivent remonter
        // TELS QUELS, pas un « HTTP 409 » opaque.
        //
        // Deux formes coexistent, et une seule etait lue :
        //
        //   { error: 'payload invalide' }                     une chaine
        //   { error: { code, message, trace_id } }            un OBJET
        //
        // La seconde est celle de core-api sur toutes ses routes gardees. En
        // ne testant que `typeof === 'string'`, l'ecran jetait le message et
        // affichait « HTTP 403 sur https://… » - or l'API disait exactement
        // « Role viewer insuffisant (analyst requis) ». Le diagnostic etait
        // dans la reponse depuis le debut, et personne ne pouvait le lire.
        const err = payload && typeof payload === 'object' ? (payload as any).error : null
        const serverMsg = typeof err === 'string'
          ? err
          : err && typeof err === 'object' && typeof err.message === 'string'
            ? err.message
            : null
        throw new ApiError(serverMsg ?? `HTTP ${res.status} sur ${url}`, res.status, payload)
      }

      return payload as T
    }),
    catchError((err) => throwError(() => err)),
  )
}
